import { useSelectedArtwork, useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import { useEffect, useState } from 'react';

export interface NGASearchResult {
    imageUrl: string;
    title?: string;
    id?: string;
    artist?: string;
    date?: string;
    medium?: string;
    dimensions?: string;
    accessionNumber?: string;
}

interface ArtGalleryState {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    ngaResults: NGASearchResult[];
    selectedArtwork: NGASearchResult | null;
    setSelectedArtwork: (artwork: NGASearchResult | null) => void;
    searchNGA: (query: string) => Promise<void>;
    loading: boolean;
    preserveAspectRatio: boolean;
    setPreserveAspectRatio: (preserve: boolean) => void;
    header: string;
    setHeader: (header: string) => void;
    footer: string;
    setFooter: (footer: string) => void;
}

export const useArtGalleryState = (onImageSelect: (content: any) => void): ArtGalleryState => {
    const [searchQuery, setSearchQuery] = useState('');
    const [ngaResults, setNgaResults] = useState<NGASearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedArtwork, setSelectedArtwork] = useSelectedArtwork();
    const [preserveAspectRatio, setPreserveAspectRatio] = useState<boolean>(false);
    const [header, setHeader] = useState('');
    const [footer, setFooter] = useState('');
    const [editedWidget] = useEditedWidget();

    // Initialize preserve aspect ratio from edited widget if available
    useEffect(() => {
        if (editedWidget?.content?.aspectRatio) {
            setPreserveAspectRatio(editedWidget.content.aspectRatio === 'original');
        } else {
            // Reset to false for new widgets
            setPreserveAspectRatio(false);
        }
    }, [editedWidget]);

    const searchNGA = async (query: string) => {
        if (!query.trim()) return;
        setLoading(true);

        try {
            const endpoints = [
                `https://api.nga.gov/art?q=${encodeURIComponent(query)}&limit=12&fields=id,title,artist,images,medium,dimensions,displaydate,accessionNumber`,
                `https://www.nga.gov/api/v1/artworks/search?q=${encodeURIComponent(query)}&limit=12`,
                `https://api.artic.edu/api/v1/artworks/search?q=${encodeURIComponent(
                    query
                )}&limit=12&fields=id,title,artist_display,image_id,date_display,medium_display,dimensions`
            ];

            let data: any = null;
            let results: NGASearchResult[] = [];

            for (const endpoint of endpoints) {
                try {
                    const response = await fetch(endpoint);
                    if (response.ok) {
                        data = await response.json();
                        break;
                    }
                } catch {
                    continue;
                }
            }

            if (data && data.data && Array.isArray(data.data)) {
                results = data.data
                    .filter((item: any) => item.images || item.image_id)
                    .slice(0, 9)
                    .map((item: any, index: number) => {
                        const imageUrl = item.images?.[0]?.iiif
                            ? `https://api.nga.gov/iiif/${item.images[0].iiif}/full/400,/0/default.jpg`
                            : item.image_id
                            ? `https://www.artic.edu/iiif/2/${item.image_id}/full/400,/0/default.jpg`
                            : `/placeholder.svg?height=200&width=350&text=${encodeURIComponent(query)}+${index + 1}`;

                        return {
                            id: item.id?.toString() || `api-${index}`,
                            title: item.title || item.title_display || `${query} Artwork ${index + 1}`,
                            artist: item.artist || item.artist_display || 'Unknown Artist',
                            imageUrl,
                            date: item.displaydate || item.date_display || '',
                            medium: item.medium || item.medium_display || 'Mixed media',
                            dimensions: item.dimensions || item.dimensions_display || '',
                            accessionNumber: item.accessionNumber || item.main_reference_number || `REF.${Date.now()}.${index}`
                        };
                    });
            }

            if (results.length === 0) throw new Error('No results from APIs');

            setNgaResults(results);
        } catch {
            setNgaResults([
                {
                    id: 'fallback-1',
                    title: `${query} - Masterpiece`,
                    artist: 'Fallback Artist',
                    imageUrl: `/placeholder.svg?height=200&width=350&text=${encodeURIComponent(query)}+Fallback`,
                    date: 'Unknown',
                    medium: 'Mixed media',
                    dimensions: 'Various',
                    accessionNumber: `FB-${Date.now()}`
                }
            ]);
        }

        setLoading(false);
    };

    useEffect(() => {
        // Use consistent as default, original when preserveAspectRatio is true
        const finalAspectRatio = preserveAspectRatio ? 'original' : 'consistent';

        if (selectedArtwork) {
            onImageSelect({
                imageUrl: selectedArtwork.imageUrl,
                title: selectedArtwork.title,
                artist: selectedArtwork.artist,
                date: selectedArtwork.date,
                medium: selectedArtwork.medium,
                aspectRatio: finalAspectRatio
            });
        } else if (editedWidget?.content) {
            onImageSelect({
                imageUrl: editedWidget.content.imageUrl,
                title: editedWidget.content.title,
                artist: editedWidget.content.artist,
                date: editedWidget.content.date,
                medium: editedWidget.content.medium,
                aspectRatio: finalAspectRatio
            });
        }
    }, [selectedArtwork, editedWidget?.content, preserveAspectRatio]);

    return {
        searchQuery,
        setSearchQuery,
        ngaResults,
        selectedArtwork,
        setSelectedArtwork,
        searchNGA,
        loading,
        preserveAspectRatio,
        setPreserveAspectRatio,
        header,
        setHeader,
        footer,
        setFooter
    };
};
